<template>
  <div class="container profile-edit-modal">
    <div class="profile-edit-header f-h f-ac f-sb p-bx2 m-bx5 bg-white">
      <donut-modal-sections-title title="About me" class="t-xl f-1 f-js bold" style="margin-bottom: 0" />
      <div class="f-1 f-je">
        <donut-modal-sections-actions
          :show-cancel="true"
          cancel-text="Close"
          save-action="userProfileFields/updateEntries"
          :save-args="{ userId: profileUser.id, entries: currentEntries, userManagerRelationship: collectManagerFromFields(profileUser.id) }"
          :is-save-disabled="Object.values(state.errors || {}).some(error => error != null)"
          :loading-on-save="true"
          :save-tracking="{
            eventName: 'User Profiles - Saved Profile Edit',
            properties: {
              num_entries: currentEntries.length,
              num_filled_entries: currentEntries.filter(entry => entry.text).length,
              filled_field_ids: currentEntries.filter(entry => entry.text).map(entry => entry.field_id)
            }
          }"
          :cancel-tracking="{
            eventName: 'User Profiles - Closed Profile Edit Modal',
            properties: {
              num_entries: currentEntries.length,
              num_filled_entries: currentEntries.filter(entry => entry.text).length,
              filled_field_ids: currentEntries.filter(entry => entry.text).map(entry => entry.field_id)
            }
          }"
        />
      </div>
    </div>
    <div class="t description">
      Donut uses your answers try to match you with people who share your interests, experiences, or goals based on your Intros settings — so you can have more meaningful conversations.
    </div>
    <div class="grid vueified m-tx3">
      <div class="t bold xxl">
        <div v-emoji="':information_source: Info'" />
      </div>
      <div class="body">
        <user-profiles-modals--edit-profile-info
          ref="profileInfoSection"
          :profile-entries="profileEntries"
          :profile-user="profileUser"
          :form-submitted="state.formSubmitted"
        />
        <user-profiles-modals--edit-about-me
          ref="aboutMeSection"
          :profile-entries="profileEntries"
          :form-submitted="state.formSubmitted"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { onMounted, computed, ref } from 'vue';
import useValidators from 'composables/validators';

const { state, getters, dispatch } = useStore();
const { characterCountValidator } = useValidators();

const formIsInvalid = ref(false);
const modalData = computed(() => state.modal.data);
const profileUser = computed(() => modalData.value.profileUser);
const profileEntries = computed(() => modalData.value.profileEntries);
const profileInfoSection = ref(null);
const aboutMeSection = ref(null);

const collectEntriesFromFields = (fields, userId) => fields
  .filter(field => field.id && field.input_type !== 'not_editable' && field.input_type !== 'user')
  .map(field => ({
    text: field.value.value,
    user_id: userId,
    field_id: field.id,
  }));

const collectManagerFromFields = (userId) => {
  if (profileInfoSection.value) {
    const managerField = profileInfoSection.value.infoSection.fields
      .find(field => field.id === 'reports_to' && field.input_type === 'user');
    return managerField ? managerField.value.value : null;
  }
  return null;
};

onMounted(() => {
  // Track modal open
  window.donut.track('User Profiles - Opened Profile Edit Modal', {
    num_entries: profileEntries.value.length,
    num_filled_entries: profileEntries.value.filter(entry => entry.text).length,
    filled_field_ids: profileEntries.value.filter(entry => entry.text).map(entry => entry.field_id),
  });
});

const currentEntries = computed(() => {
  const entries = [];
  const userId = profileUser.value.id;
  // Get entries from profile info section
  if (profileInfoSection.value) {
    entries.push(...collectEntriesFromFields(profileInfoSection.value.infoSection.fields, userId));
    entries.push(...collectEntriesFromFields(profileInfoSection.value.bioSection.fields, userId));
  }
  // Get entries from about me section
  if (aboutMeSection.value && aboutMeSection.value.sections) {
    aboutMeSection.value.sections.forEach(section => {
      section.value.categories.forEach(category => {
        entries.push(...collectEntriesFromFields(category.fields, userId));
      });
    });
  }
  return entries;
});
</script>

<style scoped lang="scss">
  .profile-edit-modal {
    position: relative;
  }

  .profile-edit-header {
    position: sticky;
    top: -24px;
    z-index: 2;
    background: white;
  }

  .grid {
    position: relative;
    z-index: 1;
  }
</style>
